import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { ScoreBoard, ScoreBoardTypes } from '@/modules/battles/interfaces/ScoreBoard';
import type { BattleCommentsType } from '@/modules/battles/interfaces/BattleComment';

const resultsInitState: ScoreBoard = {
  trivium: {
    technique: 0,
    variety: 0,
    performance: 0,
    musicality: 0,
    creativity: 0,
    personality: 0,
  },
  threefold: {
    physicalQuality: 0,
    artisticQuality: 0,
    interpretiveQuality: 0,
  },
  classic: {
    winner: 0,
  },
  typeB: {
    physical: 0,
    interpretative: 0,
    artistic: 0,
  },
  typeA: {
    technique: 0,
    vocabulary: 0,
    originality: 0,
    execution: 0,
    musicality: 0,
  },
};

export const useResultStore = defineStore('result', () => {
  const result = ref<ScoreBoard>(JSON.parse(JSON.stringify(resultsInitState)));
  const currentJudgePanelType = ref<BattleCommentsType>('threefold');

  const setJudgePanelType = (type: BattleCommentsType) => {
    currentJudgePanelType.value = type;
  };

  const setResult = (action: {
    type: string;
    payload?: {
      type?: string;
      team?: string;
      value?: number;
      boardResult?: ScoreBoard[keyof ScoreBoard];
      types?: string[];
    };
  }) => {
    if (action.type === 'reset') {
      result.value = JSON.parse(JSON.stringify(resultsInitState));
      return;
    }

    const panelResult = result.value[currentJudgePanelType.value];
    if (!panelResult) return;

    if (action.type === 'setBoard' && action.payload?.boardResult) {
      (result.value as Record<BattleCommentsType, ScoreBoardTypes>)[currentJudgePanelType.value] = action.payload.boardResult;
      return;
    }

    // Handle addMany and subtractMany actions
    if (action.type === 'addMany' && action.payload?.types && action.payload.value !== undefined) {
      const operation = action.payload.team === 'blueSide';
      const updatedResult: any = { ...panelResult };
      
      action.payload.types.forEach((type) => {
        const currentValue = (panelResult as any)[type] || 0;
        const newValue = operation 
          ? currentValue + action.payload!.value! 
          : currentValue - action.payload!.value!;
        updatedResult[type] = newValue;
      });
      
      result.value[currentJudgePanelType.value] = updatedResult;
      return;
    }

    if (action.type === 'subtractMany' && action.payload?.types && action.payload.value !== undefined) {
      const operation = action.payload.team === 'blueSide';
      const updatedResult: any = { ...panelResult };
      
      action.payload.types.forEach((type) => {
        const currentValue = (panelResult as any)[type] || 0;
        const newValue = operation 
          ? currentValue - action.payload!.value! 
          : currentValue + action.payload!.value!;
        updatedResult[type] = newValue;
      });
      
      result.value[currentJudgePanelType.value] = updatedResult;
      return;
    }

    if (action.type.startsWith('set') && action.payload) {
      const fieldName = action.type.replace('set', '');
      const firstChar = fieldName.charAt(0).toLowerCase();
      const rest = fieldName.slice(1);
      const camelCaseField = firstChar + rest;

      // Handle team-based updates (redSide/blueSide)
      if (action.payload.team && action.payload.value !== undefined) {
        let newValue: number;

        if (action.payload.team === 'redSide') {
          // Red side is negative
          newValue = -Math.abs(action.payload.value);
        } else {
          // Blue side is positive
          newValue = Math.abs(action.payload.value);
        }

        // Create new objects to trigger reactivity
        result.value = {
          ...result.value,
          [currentJudgePanelType.value]: {
            ...panelResult,
            [camelCaseField]: newValue,
          },
        };
        return;
      } else if (action.payload.value !== undefined && camelCaseField in panelResult) {
        result.value = {
          ...result.value,
          [currentJudgePanelType.value]: {
            ...panelResult,
            [camelCaseField]: action.payload.value,
          },
        };
        return;
      }
    }

    // Handle add/subtract actions with team
    if ((action.type.startsWith('add') || action.type.startsWith('subtract')) && action.payload) {
      const fieldName = action.type.replace(/^(add|subtract)/, '');
      const firstChar = fieldName.charAt(0).toLowerCase();
      const rest = fieldName.slice(1);
      const camelCaseField = firstChar + rest;

      if (action.payload.team && action.payload.value !== undefined && camelCaseField in panelResult) {
        const operation = action.payload.team === 'blueSide';
        const currentValue = (panelResult as any)[camelCaseField] || 0;
        let newValue = currentValue;

        if (action.type.startsWith('add')) {
          newValue = operation 
            ? currentValue + action.payload.value 
            : currentValue - action.payload.value;
        } else if (action.type.startsWith('subtract')) {
          newValue = operation 
            ? currentValue - action.payload.value 
            : currentValue + action.payload.value;
        }

        // Create new objects to trigger reactivity
        result.value = {
          ...result.value,
          [currentJudgePanelType.value]: {
            ...panelResult,
            [camelCaseField]: newValue,
          },
        };
      }
    }
  };

  const resetResult = () => {
    result.value = JSON.parse(JSON.stringify(resultsInitState));
  };

  const resultForCurrentJudgePanel = computed(() => {
    return result.value[currentJudgePanelType.value] || {};
  });

  return {
    result: computed(() => result.value || JSON.parse(JSON.stringify(resultsInitState))),
    currentJudgePanelType: computed(() => currentJudgePanelType.value),
    resultForCurrentJudgePanel,
    setJudgePanelType,
    setResult,
    resetResult,
  };
});

